import { IdHelper } from '../helpers/id.helper';
import type { ILine } from '../models/line.model';
import type { IMunicipality } from '../models/municipality.model';
import type { ITrafficSituation } from '../models/traffic-situation.model';
import type { TransportMode } from '../models/transport-mode.enum';
import { ClientSideStorageItems, ClientSideStorageService } from './client-side-storage.service';
import { ConfigurationService } from './configuration.service';

enum UserFeatureName {
  municipality = 'municipality',
  line = 'line',
  transportMode = 'transportmode',
}

export class TrafficInformationFeedbackService {
  private static enabled: boolean | undefined = undefined;
  private static userId: string | undefined;

  public static isEnabled() {
    if (this.enabled === undefined) {
      this.enabled = ConfigurationService.get('featureflags.trafficinformation.feedbackcollection') === 'True';
    }
    return this.enabled;
  }

  public static async registerImplicitFeedback(trafficSituation: ITrafficSituation) {
    if (!TrafficInformationFeedbackService.isEnabled()) return;

    const userId = TrafficInformationFeedbackService.getUserId();
    if (!userId) return;

    await fetch(`/api/trafficsituations/${trafficSituation.situationNumber}/feedback/implicit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: 0.1, userId }),
    });
  }

  public static async registerExplicitFeedback(trafficSituation: ITrafficSituation, value: number) {
    if (!TrafficInformationFeedbackService.isEnabled()) return;

    const userId = TrafficInformationFeedbackService.getUserId();
    if (!userId) return;

    await fetch(`/api/trafficsituations/${trafficSituation.situationNumber}/feedback/explicit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value, userId }),
    });
  }

  public static async registerUserFeature(name: string, value: string, weight: number) {
    if (!TrafficInformationFeedbackService.isEnabled()) return;

    const userId = TrafficInformationFeedbackService.getUserId();
    if (!userId) return;

    await fetch(`/api/trafficsituations/feedback/userfeatures`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, features: [{ name, value, weight }] }),
    });
  }

  public static registerSelectedLine(line: ILine): void {
    if (!TrafficInformationFeedbackService.isEnabled() || !line.gid) return;
    TrafficInformationFeedbackService.registerUserFeature(UserFeatureName.line, line.gid, 1);
  }

  public static registerSelectedMunicipalities(oldMunicipalities: IMunicipality[], newMunicipalities: IMunicipality[]): void {
    if (!TrafficInformationFeedbackService.isEnabled()) return;
    const addedMunicipalities = newMunicipalities.filter((m) => !oldMunicipalities.includes(m));
    const removedMunicipalities = oldMunicipalities.filter((m) => !newMunicipalities.includes(m));
    addedMunicipalities.forEach(async (m) => {
      TrafficInformationFeedbackService.registerUserFeature(UserFeatureName.municipality, m.gid, 1);
    });
    removedMunicipalities.forEach(async (m) => {
      TrafficInformationFeedbackService.registerUserFeature(UserFeatureName.municipality, m.gid, -1);
    });
  }

  public static registerSelectedTransportModes(oldTransportModes: TransportMode[], newTransportModes: TransportMode[]): void {
    if (!TrafficInformationFeedbackService.isEnabled()) return;
    const addedTransportModes = newTransportModes.filter((m) => !oldTransportModes.includes(m));
    const removedTransportModes = oldTransportModes.filter((m) => !newTransportModes.includes(m));
    addedTransportModes.forEach(async (m) => {
      TrafficInformationFeedbackService.registerUserFeature(UserFeatureName.transportMode, m, 1);
    });
    removedTransportModes.forEach(async (m) => {
      TrafficInformationFeedbackService.registerUserFeature(UserFeatureName.transportMode, m, -1);
    });
  }

  private static getUserId(): string | undefined {
    if (!ClientSideStorageService.isConsentGiven(ClientSideStorageItems.LocalStorage.TrafficInformationFeedbackUserId.consentStatus)) return undefined;

    if (this.userId) return this.userId;

    try {
      this.userId = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TrafficInformationFeedbackUserId);
      if (this.userId) return this.userId;
    } catch {
      /* empty */
    }

    try {
      this.userId = IdHelper.newGuid();
      ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.TrafficInformationFeedbackUserId, this.userId);
      return this.userId;
    } catch {
      return undefined;
    }
  }
}
